import { useQuery } from '@apollo/client';
import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from 'src/auth/hooks';
import { GET_ORGANIZATIONS_FOR_MANAGER } from 'src/graphql/organizations/queries';
import { useLocalStorage } from 'src/hooks/use-local-storage';
import { getAllowedGeoScopes } from 'src/utils/geo-scopes';
import { isCrossOrgRole } from 'src/utils/roles';
import {
  findGoverningMembership,
  getEligibleOrganizations,
  isOrganizationActive,
  isOrganizationCurrent,
} from 'src/utils/organization-helper';

export const OrganizationContext = createContext();

export const OrganizationContextProvider = ({ children }) => {
  const { user } = useAuthContext();
  const globalRole = user?.users_user_roles?.[0]?.user_roles_roles?.code;
  const isCrossOrg = isCrossOrgRole(globalRole);
  const [value, setDefaultOrganizationInContext] = useLocalStorage('organization-context');
  const {
    data: organizations_data,
    loading: organizationsLoading,
    refetch,
  } = useQuery(GET_ORGANIZATIONS_FOR_MANAGER, {
    skip: isCrossOrg || !user?.user_id,
    variables: { user_id: user?.user_id },
  });
  // Only the orgs this account may act on in web — see getEligibleOrganizations. Everything
  // downstream (selector, default org, geo scopes) reads this, never the raw query rows.
  const organizations = useMemo(
    () => getEligibleOrganizations(organizations_data?.rows, globalRole, user?.user_id),
    [organizations_data?.rows, globalRole, user?.user_id]
  );

  const [selectedOrganization, setSelectedOrganization] = useState();
  const [roleInSelectedOrg, setRoleInSelectedOrg] = useState();
  const [isOrgActive, setIsOrgActive] = useState(true);
  const [isPrimaryUser, setIsPrimaryUser] = useState(false);
  const [isManualSelection, setIsManualSelection] = useState(false);

  /**
   * The row for the selected org, expired or not. Membership state is reported separately as
   * isOrgActive; filtering an expired org out here would leave Billing — the one section that
   * stays open to an expired org, see isLockExemptPath — reading a null org and telling a
   * paying customer they have no billing details on file.
   *
   * Only eligible orgs ever reach `organizations`, so this cannot resolve an org the user has
   * no business seeing.
   */
  const selectedOrgData = useMemo(
    () => organizations?.find((row) => selectedOrganization === row?.organization_id),
    [organizations, selectedOrganization]
  );

  // Helper function to set organization state
  const setOrganizationState = useCallback(
    (org) => {
      if (!org) return;

      // Same membership row that made this org eligible, so the selector and the role can't
      // disagree — see findGoverningMembership.
      const role = findGoverningMembership(org, globalRole, user?.user_id)
        ?.user_organizations_role?.code;

      setSelectedOrganization(org?.organization_id);
      setDefaultOrganizationInContext(org?.organization_id);
      setRoleInSelectedOrg(role || '');
      setIsOrgActive(isOrganizationActive(org) && isOrganizationCurrent(org));
      setIsPrimaryUser(org?.email === user?.email);
    },
    [globalRole, user?.user_id, user?.email, setDefaultOrganizationInContext]
  );

  // Helper function to find first current and active organization
  const findFirstCurrentOrg = useCallback(() => {
    return organizations?.find((row) => isOrganizationCurrent(row) && isOrganizationActive(row));
  }, [organizations]);

  useEffect(() => {
    try {
      if (!organizations?.length) return;

      const storedOrg =
        value && organizations?.find((row) => String(row?.organization_id) === String(value));

      if (storedOrg) {
        const isOrgCurrent = isOrganizationCurrent(storedOrg);

        // If this is not a manual selection and the org is not current,
        // find the first current org instead
        if (!isManualSelection && !isOrgCurrent) {
          const firstCurrentOrg = findFirstCurrentOrg();
          setOrganizationState(firstCurrentOrg || organizations?.[0]);
        } else {
          // Use the stored org (either manual selection or current org)
          setOrganizationState(storedOrg);
        }
      } else {
        // No stored org or stored org not found, find first current org
        const firstCurrentOrg = findFirstCurrentOrg();
        setOrganizationState(firstCurrentOrg || organizations?.[0]);
      }
    } catch (error) {
      console.error('Error processing organization data:', error);
      // Reset to safe defaults
      setSelectedOrganization(null);
      setRoleInSelectedOrg('');
      setIsOrgActive(false);
      setIsPrimaryUser(false);
    }
  }, [
    organizations,
    value,
    user?.user_id,
    user?.email,
    isManualSelection,
    setOrganizationState,
    findFirstCurrentOrg,
  ]);

  useEffect(() => {
    if (user?.user_id) {
      refetch();
    }
  }, [user?.user_id, refetch]);

  // Reset manual selection flag when organizations data changes (fresh load)
  useEffect(() => {
    if (organizations?.length) {
      setIsManualSelection(false);
    }
  }, [organizations]);

  // Whether the selected organization — and so roleInSelectedOrganization — has settled. Until it
  // has, the effective role falls back to the global role, which says `user` for many managers
  // (see backend/src/services/auth/cognito-pre-token.ts), so anything that navigates on the role
  // must wait for this. Also settled when there is nothing to select — the query failed or found
  // no eligible organization — so a waiter never hangs.
  const isSelectionResolved =
    selectedOrganization !== undefined || (!organizationsLoading && !organizations?.length);

  const geoScopes = useMemo(() => {
    if (isCrossOrg) {
      return {};
    }
    const tokenPayload = user?.signInUserSession?.idToken?.payload;
    return getAllowedGeoScopes(tokenPayload) || {};
  }, [user, isCrossOrg]);

  return (
    <>
      <OrganizationContext.Provider
        value={
          isCrossOrg
            ? {
              organizations: [],
              selectedOrganizationId: 0,
              selectedOrgData: null,
              roleInSelectedOrganization: '',
              isSelectionResolved: true,
              isOrgActive: true,
              setSelectedOrganization: (id) => { },
              geoScopes,
              isPrimaryUser: true,
            }
            : {
              organizations,
              selectedOrganizationId: selectedOrganization,
              selectedOrgData,
              roleInSelectedOrganization: roleInSelectedOrg,
              isSelectionResolved,
              isOrgActive: isOrgActive,
              setSelectedOrganization: (id) => {
                setIsManualSelection(true);
                setSelectedOrganization(id);
                setDefaultOrganizationInContext(id);
              },
              geoScopes,
              isPrimaryUser,
            }
        }
      >
        {children}
      </OrganizationContext.Provider>
    </>
  );
};

export const useOrganizationContext = () => {
  return useContext(OrganizationContext);
};
